import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import API from "../../api";
import "../css/ShowDocumnetModal.css";

/** Pull user fields from JWT in local/session storage */
function getUserFromToken() {
  const tok =
    window.localStorage.getItem("token") ||
    window.sessionStorage.getItem("token");
  if (!tok) return {};
  const parts = tok.split(".");
  if (parts.length < 2) return {};
  try {
    const json = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return json?.user || json || {};
  } catch {
    return {};
  }
}

/** token first -> redux-persist fallback */
// function getUsernameAndRole() {
//   const u = getUserFromToken();
//   const usernameFromToken =
//     u?.username || u?.name || u?.preferred_username || "";
//   const roleFromToken = (u?.role || "").toLowerCase();

//   if (usernameFromToken || roleFromToken) {
//     return {
//       username: String(usernameFromToken || ""),
//       role: String(roleFromToken || ""),
//     };
//   }

//   const keysToTry = ["persistroot", "persist:root"];
//   for (const key of keysToTry) {
//     try {
//       const raw = window.localStorage.getItem(key);
//       if (!raw) continue;

//       const root = JSON.parse(raw);
//       const authObj =
//         typeof root?.auth === "string" ? JSON.parse(root.auth) : root?.auth;

//       const username =
//         authObj?.user?.username ||
//         authObj?.user?.name ||
//         authObj?.username ||
//         "";

//       const role = (authObj?.user?.role || authObj?.role || "").toLowerCase();

//       return { username: String(username || ""), role: String(role || "") };
//     } catch {
//       // try next
//     }
//   }
//   return { username: "", role: "" };
// }


/** redux-persist first (role must come from persist:root) -> token fallback */
function getUsernameAndRole() {
  // ✅ 1) Prefer redux-persist role ALWAYS (your requirement)
  const keysToTry = ["persist:root", "persistroot"];
  for (const key of keysToTry) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      const root = JSON.parse(raw);

      // root.auth is usually a JSON string
      const authObj =
        typeof root?.auth === "string" ? JSON.parse(root.auth) : root?.auth;

      const username =
        authObj?.user?.username ||
        authObj?.user?.name ||
        authObj?.username ||
        "";

      const role = (authObj?.user?.role || authObj?.role || "").toLowerCase();

      // if persist has role/username, return it
      if (username || role) {
        return { username: String(username || ""), role: String(role || "") };
      }
    } catch {
      // try next key
    }
  }

  // ✅ 2) Token fallback (only if persist not available)
  const u = getUserFromToken();
  const usernameFromToken =
    u?.username || u?.name || u?.preferred_username || "";
  const roleFromToken = (u?.role || "").toLowerCase();

  return {
    username: String(usernameFromToken || ""),
    role: String(roleFromToken || ""),
  };
}



// hasOwnProperty helper
function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

// Hydrate helper: if key exists and value is null/undefined => ""
function pick(data, key, fallback = "") {
  if (!hasOwn(data, key)) return fallback;
  const v = data[key];
  return v === null || typeof v === "undefined" ? "" : v;
}

// number hydrate helper: if key exists and null/undefined => 0
function pickNum(data, key, fallback = 0) {
  if (!hasOwn(data, key)) return fallback;
  const v = data[key];
  if (v === null || typeof v === "undefined" || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isBlank(v) {
  return !String(v ?? "").trim();
}

function toDateInput(v) {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  return String(v);
}

/**
 * Convert many common user-entered formats into MySQL DATE yyyy-mm-dd.
 * Accepts:
 *  - yyyy-mm-dd (returns as-is)
 *  - mm-dd-yyyy / mm/dd/yyyy
 */
function toMysqlDate(v) {
  const s = String(v || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const mm = String(m[1]).padStart(2, "0");
    const dd = String(m[2]).padStart(2, "0");
    const yyyy = String(m[3]);
    return `${yyyy}-${mm}-${dd}`;
  }

  return s;
}

function isValidSSNOrBlank(ssn) {
  const s = String(ssn || "").trim();
  if (!s) return true;
  return /^\d{9}$/.test(s);
}

/**
 * Returns true if the value is blank OR matches an accepted date format.
 * Accepted: YYYY-MM-DD | MM-DD-YYYY | MM/DD/YYYY
 * Also does a real calendar check (e.g. rejects 02-30-2025).
 */
function isValidDateValue(v) {
  const s = String(v || "").trim();
  if (!s) return true;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    return !isNaN(d.getTime());
  }

  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const iso = `${m[3]}-${String(m[1]).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
    const d = new Date(`${iso}T00:00:00`);
    return !isNaN(d.getTime());
  }

  return false;
}

export default function ShowDocumnetModal({
  open,
  onClose,
  lead, // { lead_id, name, bname, email, mobile/phone, agent }
  onSaved,
}) {
  const leadId = useMemo(
    () => lead?.lead_id || lead?.leadId || lead?.id || null,
    [lead]
  );

  // const statusOptions = useMemo(
  //   () => [
  //     "sent",
  //     "signed",
  //     "approved",
  //     "HF-Filled",
  //     "submitted",
  //     "Pending Approval",
  //     "Declined",
  //   ],
  //   []
  // );


  //  const statusOptions = useMemo(
  //   () => [
  //     "HF-Filled",
  //     "Declined",
  //     "Uploaded to Pdx",
  //     "Approved",
  //     "Signed",
  //     "Submitted",
  //     "Pending Approval",
  //   ],
  //   []
  // );


  const { role } = useMemo(() => getUsernameAndRole(), []);
  const isAdmin = role === "admin";
  const reduxUser = useSelector(selectUser);

  const statusOptions = useMemo(() => {
    const all = [
      "HF-Filled",
      "Declined",
      "Uploaded to Pdx",
      "Approved",
      "Signed",
      "Submitted",
      "Pending Approval",
    ];

    const subAdminOnly = ["HF-Filled", "Declined", "Uploaded to Pdx"];

    return role === "sub-admin" ? subAdminOnly : all;
  }, [role]);


  const equipmentKeys = useMemo(
    () => [
      "paxa920",
      "dejavoo-qd4",
      "clover-go",
      "clover-flex",
      "clover-mini",
      "clover-mini-with-Cashdrawer",
      "clover-station",
      "clover-station-duo",
    ],
    []
  );

  const emptyEquipmentCounts = useMemo(() => {
    const obj = {};
    equipmentKeys.forEach((k) => (obj[k] = 0));
    return obj;
  }, [equipmentKeys]);

  const buildInitialForm = useMemo(() => {
    return () => ({
      lead_id: leadId || "",
      name: lead?.name || "",
      bname: lead?.bname || "",
      agent: lead?.agent || "",
      email: lead?.email || "",
      phone: lead?.mobile || lead?.phone || "",

      // owner address
      street: "",
      city: "",
      state: "",
      postal_code: "",
      DOB: "",
      SSN: "",

      // business
      "Legal-Name": "",
      "DBA-Name": "",
      "Tax-id": "",
      Website: "",
      "ownership-type": "",
      "Business-Address": "",
      "Business-City": "",
      "Business-State": "",
      "Business-zip": "",
      "Business-Phone": "",
      "Business-start-date": "",
      "Avg-ticket": "",
      "High-ticket": "",
      "total-monthly-processing": "",
      "products-sold": "",

      // pricing
      feestructure: "",
      Pricing: "",
      PricingText: "",
      "Pricing-variable": "",

      // ✅ ADD THESE
      "monthly-fee": "",
      "monthly-minimum": "",

      // banking
      "Bank-name": "",
      "Bank-acc": "",
      Routing: "",

      // equipment + shipping
      equipement: "",
      ...emptyEquipmentCounts,
      "Shipping-Address": "",
      "Shipping-City": "",
      "Shipping-State": "",
      "Shipping-zip": "",

      status: "sent",
      merchant_id: "",
      approved_by: "",
      tracking: "",
    });
  }, [leadId, lead, emptyEquipmentCounts]);

  const [form, setForm] = useState(() => buildInitialForm());
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leadFlags, setLeadFlags] = useState({ is_reprogram: 0, is_duplicate: 0, source_lead_id: null });

  const [bannerErr, setBannerErr] = useState("");
  const [bannerOk, setBannerOk] = useState("");

  // const [attemptedSave, setAttemptedSave] = useState(false);
  const [attemptedUpload, setAttemptedUpload] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [ssnOptional, setSsnOptional] = useState(false);
  const [sameAsOwner, setSameAsOwner] = useState(false);

  const [uploading, setUploading] = useState(false);

  // Admin-only: createdby override
  const [allUsers, setAllUsers] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState("");

  useEffect(() => {
    if (!isAdmin || !reduxUser?.id) return;
    API.get("/auth/admin/users-redux", { params: { requesterId: reduxUser.id } })
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : (r?.data?.users || []);
        setAllUsers(list);
      })
      .catch(() => {});
  }, [isAdmin, reduxUser?.id]);

  // Shipping address source (matches DocumentModal.jsx)
  const [shippingSource, setShippingSource] = useState("manual");



  function applyShippingSource(src, currentForm) {
    const f = { ...currentForm };

    if (src === "business") {
      f["Shipping-Address"] = f["Business-Address"] || "";
      f["Shipping-City"] = f["Business-City"] || "";
      f["Shipping-State"] = f["Business-State"] || "";
      f["Shipping-zip"] = f["Business-zip"] || "";
      return f;
    }

    if (src === "home") {
      f["Shipping-Address"] = f.street || "";
      f["Shipping-City"] = f.city || "";
      f["Shipping-State"] = f.state || "";
      f["Shipping-zip"] = f.postal_code || "";
      return f;
    }

    // manual: don't overwrite
    return f;
  }


  function update(field) {
    return (e) => {
      const val = e?.target?.value ?? e;
      setForm((f) => ({ ...f, [field]: val }));


      // clear field error if user edits after attempting save

      // if (attemptedSave && fieldErrors[field]) {
      if (attemptedUpload && fieldErrors[field]) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    };
  }

  function hasError(field) {
    // Date fields show on-blur errors immediately; other fields only after upload attempt
    const dateFields = ["DOB", "Business-start-date"];
    if (dateFields.includes(field)) return !!fieldErrors[field];
    return attemptedUpload && !!fieldErrors[field];
  }

  function extractApiError(err) {
    const res = err?.response;
    if (res?.data?.error) return res.data.error;
    if (res?.data?.message) return res.data.message;
    if (err?.message) return err.message;
    return "Request failed.";
  }

  // Reset on open/lead change
  useEffect(() => {
    if (!open) return;

    setBannerErr("");
    setBannerOk("");
    // setAttemptedSave(false);
    setAttemptedUpload(false);
    setFieldErrors({});
    setSsnOptional(false);
    setShippingSource("manual");

    setForm(buildInitialForm());
  }, [open, leadId, buildInitialForm]);

  // Fetch by lead_id (prefill)
  useEffect(() => {
    let ignore = false;

    async function run() {
      if (!open) return;
      if (!leadId) return;

      setLoadingExisting(true);
      try {
        const [mpaResp, leadResp] = await Promise.all([
          API.get(`/auth/mpa-by-lead/${leadId}`),
          API.get(`/auth/lead/${leadId}`),
        ]);
        const data = mpaResp?.data;
        if (!data || ignore) return;

        if (!ignore) {
          const ld = leadResp?.data || {};
          setLeadFlags({
            is_reprogram: Number(ld.is_reprogram) || 0,
            is_duplicate: Number(ld.is_duplicate) || 0,
            source_lead_id: ld.source_lead_id ?? null,
          });
        }

        setForm((f) => ({
          ...f,
          lead_id: pick(data, "lead_id", f.lead_id),
          name: pick(data, "name", f.name),
          bname: pick(data, "bname", f.bname),
          email: pick(data, "email", f.email),
          phone: pick(data, "phone", f.phone),
          agent: pick(data, "agent", f.agent),

          street: pick(data, "street", f.street),
          city: pick(data, "city", f.city),
          state: pick(data, "state", f.state),
          postal_code: pick(data, "postal_code", f.postal_code),
          DOB: pick(data, "DOB", f.DOB),
          SSN: pick(data, "SSN", f.SSN),

          "Legal-Name": hasOwn(data, "Legal-Name")
            ? pick(data, "Legal-Name", "")
            : hasOwn(data, "LegalName")
              ? pick(data, "LegalName", "")
              : f["Legal-Name"],

          "DBA-Name": hasOwn(data, "DBA-Name")
            ? pick(data, "DBA-Name", "")
            : hasOwn(data, "DBAName")
              ? pick(data, "DBAName", "")
              : f["DBA-Name"],

          "Tax-id": hasOwn(data, "Tax-id")
            ? pick(data, "Tax-id", "")
            : hasOwn(data, "TaxId")
              ? pick(data, "TaxId", "")
              : f["Tax-id"],

          Website: pick(data, "Website", f.Website),
          "ownership-type": pick(data, "ownership-type", f["ownership-type"]),
          "Business-Address": pick(data, "Business-Address", f["Business-Address"]),
          "Business-City": pick(data, "Business-City", f["Business-City"]),
          "Business-State": pick(data, "Business-State", f["Business-State"]),
          "Business-zip": pick(data, "Business-zip", f["Business-zip"]),
          "Business-Phone": pick(data, "Business-Phone", f["Business-Phone"]),
          "Business-start-date": pick(
            data,
            "Business-start-date",
            f["Business-start-date"]
          ),
          "Avg-ticket": pick(data, "Avg-ticket", f["Avg-ticket"]),
          "High-ticket": pick(data, "High-ticket", f["High-ticket"]),
          "total-monthly-processing": pick(
            data,
            "total-monthly-processing",
            f["total-monthly-processing"]
          ),
          "products-sold": pick(data, "products-sold", f["products-sold"]),

          feestructure: pick(data, "feestructure", f.feestructure),
          Pricing: pick(data, "Pricing", f.Pricing),
          PricingText: pick(data, "PricingText", f.PricingText),
          "Pricing-variable": pick(data, "Pricing-variable", f["Pricing-variable"]),

          // ✅ ADD
          "monthly-fee": pick(data, "monthly-fee", f["monthly-fee"]),
          "monthly-minimum": pick(data, "monthly-minimum", f["monthly-minimum"]),

          "Bank-name": pick(data, "Bank-name", f["Bank-name"]),
          "Bank-acc": pick(data, "Bank-acc", f["Bank-acc"]),
          Routing: pick(data, "Routing", f.Routing),

          equipement: pick(data, "equipement", f.equipement),
          paxa920: pickNum(data, "paxa920", f.paxa920 ?? 0),
          "dejavoo-qd4": pickNum(data, "dejavoo-qd4", f["dejavoo-qd4"] ?? 0),
          "clover-go": pickNum(data, "clover-go", f["clover-go"] ?? 0),
          "clover-flex": pickNum(data, "clover-flex", f["clover-flex"] ?? 0),
          "clover-mini": pickNum(data, "clover-mini", f["clover-mini"] ?? 0),
            "clover-mini-with-Cashdrawer": pickNum(data, "clover-mini-with-Cashdrawer", f["clover-mini-with-Cashdrawer"] ?? 0),
          "clover-station": pickNum(
            data,
            "clover-station",
            f["clover-station"] ?? 0
          ),
          "clover-station-duo": pickNum(
            data,
            "clover-station-duo",
            f["clover-station-duo"] ?? 0
          ),

          "Shipping-Address": pick(data, "Shipping-Address", f["Shipping-Address"]),
          "Shipping-City": pick(data, "Shipping-City", f["Shipping-City"]),
          "Shipping-State": pick(data, "Shipping-State", f["Shipping-State"]),
          "Shipping-zip": pick(data, "Shipping-zip", f["Shipping-zip"]),

          status: pick(data, "status", f.status || "sent"),
          merchant_id: pick(data, "merchant_id", f.merchant_id),
          approved_by: pick(data, "approved_by", f.approved_by),
           tracking: pick(data, "tracking", f.tracking),
          createdby: pick(data, "createdby", f.createdby),

        }));

        if (data.createdby) setSelectedCreatedBy(data.createdby);
      } catch (e) {
        if (e?.response?.status !== 404) console.error(e);
      } finally {
        if (!ignore) setLoadingExisting(false);
      }
    }

    run();
    return () => {
      ignore = true;
    };
  }, [open, leadId]);

  useEffect(() => {
    if (String(form.status || "").toLowerCase() !== "approved") {
      setForm((f) => ({ ...f, merchant_id: "", approved_by: "" }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.merchant_id;
        delete next.approved_by;
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.status]);




  // fee structure logic (matches DocumentModal.jsx)
  useEffect(() => {
    const fs = form.feestructure;
    setForm((f) => {
      if (fs === "surcharging") return { ...f, PricingText: "" };
      if (fs === "interchange" || fs === "flatfee") return { ...f, Pricing: "" };
      return f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.feestructure]);

  // pricing dropdown change -> auto set Pricing-variable (matches DocumentModal.jsx)
  useEffect(() => {
    if (form.feestructure !== "surcharging") return;
    const p = String(form.Pricing || "");
    const fee = p === "3" ? "2.912" : p === "4" ? "3.8461" : "";
    setForm((f) => ({ ...f, "Pricing-variable": fee }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.Pricing, form.feestructure]);

  // equipment selection: chosen device = 1, all others = 0 (matches DocumentModal.jsx)
  function onEquipmentSelect(val) {
    const chosen = String(val || "");
    setForm((f) => {
      const next = { ...f, equipement: chosen };
      equipmentKeys.forEach((k) => (next[k] = 0));
      if (chosen && equipmentKeys.includes(chosen)) next[chosen] = 1;
      return next;
    });
  }

  function validateAll(currentForm) {
    const f = currentForm;
    const errors = {};

    const req = (field, msg) => {
      if (isBlank(f[field])) errors[field] = msg || "Required";
    };


    // 👇 ADD THIS BLOCK
    if (String(form.status || "").toLowerCase() === "approved" && !form.merchant_id) {
      errors.merchant_id = "Merchant Id is required when status is Approved";
    }

    // keep your existing error handling
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }


    // required fields (same logic as DocumentModal)
    req("email", "Email is required");
    req("phone", "Phone is required");
    req("DOB", "DOB is required");
    if (!isBlank(f.DOB) && !isValidDateValue(f.DOB)) {
      errors.DOB = "Invalid date — use MM-DD-YYYY or YYYY-MM-DD";
    }

    if (!ssnOptional) req("SSN", "SSN is required (or mark optional)");
    if (!isValidSSNOrBlank(f.SSN)) errors.SSN = "SSN must be exactly 9 digits";

    req("Legal-Name", "Legal Name is required");
    req("DBA-Name", "DBA Name is required");
    req("ownership-type", "Ownership Type is required");
    req("Business-Address", "Business Address is required");
    req("Business-start-date", "Business Start Date is required");
    if (!isBlank(f["Business-start-date"]) && !isValidDateValue(f["Business-start-date"])) {
      errors["Business-start-date"] = "Invalid date — use MM-DD-YYYY or YYYY-MM-DD";
    }

    req("feestructure", "Fee Structure is required");
    if (f.feestructure === "surcharging") {
      req("Pricing", "Pricing is required");
      req("Pricing-variable", "Fee is required");
    }
    if (f.feestructure === "interchange" || f.feestructure === "flatfee") {
      req("PricingText", "Pricing text is required");
      req("Pricing-variable", "Fee is required");
    }

    req("Bank-name", "Bank name is required");
    req("Routing", "Routing is required");

    req("products-sold", "Description of Products Sold is required");

    // status required (you can remove if optional)
    req("status", "Status is required");
    if (String(f.status || "").toLowerCase() === "approved") {
      req("merchant_id", "Merchant Id is required");
      req("approved_by", "Approved By is required");
      
    }

    return errors;
  }

 

  // async function save() {
  //   setBannerErr("");
  //   setBannerOk("");

  //   // ✅ Save should not validate anymore
  //   // also don't set attemptedUpload here
  //   setFieldErrors({});

  //   if (!leadId && isBlank(form.lead_id)) {
  //     setBannerErr("Missing lead_id.");
  //     return;
  //   }

  //   setSaving(true);
  //   try {
  //     const { username, role } = getUsernameAndRole();

  //     const payload = {
  //       ...form,
  //       lead_id: leadId || form.lead_id,
  //       createdby: username || "Unknown",
  //       role: role || "",
  //       DOB: toMysqlDate(form.DOB),
  //       "Business-start-date": toMysqlDate(form["Business-start-date"]),
  //     };

  //     const resp = await API.post("/auth/mpa-upsert", payload);

  //     setBannerOk("Saved (draft).");
  //     setTimeout(() => setBannerOk(""), 1500);

  //     onSaved?.(resp?.data || payload);
  //     onClose?.();
  //   } catch (e) {
  //     console.error(e);
  //     setBannerErr(extractApiError(e));
  //   } finally {
  //     setSaving(false);
  //   }
  // }



  // async function uploadToPandaDoc() {
  //   try {
  //     setUploading(true);

  //     const id = leadId || form.lead_id;
  //     if (!id) {
  //       alert("Missing lead_id.");
  //       return;
  //     }

  //     const resp = await API.post("/auth/mpa-upload-pandadoc", { lead_id: id });

  //     // success popup alert (your requirement)
  //     alert(`Uploaded to PandaDoc successfully!\nDocument ID: ${resp?.data?.document_id || ""}`);
  //   } catch (e) {
  //     console.error(e);
  //     const msg =
  //       e?.response?.data?.error ||
  //       e?.message ||
  //       "Upload failed";
  //     alert(msg);
  //   } finally {
  //     setUploading(false);
  //   }
  // }


  async function save() {
  setBannerErr("");
  setBannerOk("");

  // Draft save: allow incomplete forms EXCEPT when status is Approved.
  // When status is Approved, Merchant Id (and Approved By) must not be empty.
  const isApproved =
    String(form.status || "").trim().toLowerCase() === "approved";

  if (isApproved) {
    setAttemptedUpload(true); // reuse attemptedUpload to show field highlights
    const errors = {};

    if (isBlank(form.merchant_id)) {
      errors.merchant_id = "Merchant Id is required when status is Approved";
    }
    if (isBlank(form.approved_by)) {
      errors.approved_by = "Approved By is required when status is Approved";
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length) {
      setBannerErr("Please fill Merchant Id before saving an Approved record.");
      return;
    }
  } else {
    // For non-approved statuses, keep save as a draft (no full validation).
    setFieldErrors({});
  }

  if (!leadId && isBlank(form.lead_id)) {
    setBannerErr("Missing lead_id.");
    return;
  }

  setSaving(true);
  try {
    const { username, role } = getUsernameAndRole();

    const payload = {
      ...form,
      lead_id: leadId || form.lead_id,
      createdby: isAdmin && selectedCreatedBy ? selectedCreatedBy : (form.createdby || username || "Unknown"),
      role: role || "",
      DOB: toMysqlDate(form.DOB),
      "Business-start-date": toMysqlDate(form["Business-start-date"]),
    };

    const resp = await API.post("/auth/mpa-upsert", payload);

    setBannerOk("Saved (draft).");
    setTimeout(() => setBannerOk(""), 1500);

    onSaved?.(resp?.data || payload);
    onClose?.();
  } catch (e) {
    console.error(e);
    setBannerErr(extractApiError(e));
  } finally {
    setSaving(false);
  }
}

  async function uploadToPandaDoc() {
    setBannerErr("");
    setBannerOk("");

    setAttemptedUpload(true);

    const errors = validateAll(form);
    setFieldErrors(errors);

    const id = leadId || form.lead_id;
    if (!id) {
      setBannerErr("Missing lead_id.");
      return;
    }

    // Always save current values to DB first (partial data is fine)
    try {
      setUploading(true);
      const { username, role } = getUsernameAndRole();
      await API.post("/auth/mpa-upsert", {
        ...form,
        lead_id: id,
        createdby: isAdmin && selectedCreatedBy ? selectedCreatedBy : (form.createdby || username || "Unknown"),
        role: role || "",
        DOB: toMysqlDate(form.DOB),
        "Business-start-date": toMysqlDate(form["Business-start-date"]),
      });
    } catch (e) {
      console.error(e);
      setBannerErr(e?.response?.data?.error || e?.message || "Failed to save before upload");
      setUploading(false);
      return;
    }

    // If validation failed, stop here — data is already saved
    if (Object.keys(errors).length) {
      setBannerErr("Details saved. Please fill all required fields before uploading to PandaDoc.");
      setUploading(false);
      return;
    }

    // All validations passed — proceed with PandaDoc upload
    try {
      const resp = await API.post("/auth/mpa-upload-pandadoc", { lead_id: id });

      alert(
        `Uploaded to PandaDoc successfully!\nDocument ID: ${resp?.data?.document_id || ""}`
      );
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.error || e?.message || "Upload failed";
      setBannerErr(msg);
      alert(msg);
    } finally {
      setUploading(false);
    }
  }


  if (!open) return null;

  const RequiredStar = () => <span className="sdm-required">*</span>;

  return (
    <div className="sdm-backdrop"  role="presentation">
      <div
        className={`sdm-drawer ${open ? "sdm-open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sdm-header">
          <div className="sdm-header-title">
            <div className="sdm-bname">{form.bname || "Business"}</div>
            <div className="sdm-sub" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>Lead ID: {leadId || form.lead_id || "—"}</span>
              {(Number(leadFlags.is_duplicate) === 1 || Number(lead?.is_duplicate) === 1) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#ede9fe',
                    color: '#5b21b6',
                    border: '1px solid #c4b5fd',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 10px',
                    letterSpacing: 0.3,
                  }}
                  title={`This document belongs to a duplicate lead (original Lead #${leadFlags.source_lead_id || lead?.source_lead_id})`}
                >
                  ⧉ DUPLICATE of #{leadFlags.source_lead_id || lead?.source_lead_id || '—'}
                </span>
              )}
              {(Number(leadFlags.is_reprogram) === 1 || Number(lead?.is_reprogram) === 1) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#fef3c7',
                    color: '#92400e',
                    border: '1px solid #fcd34d',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 10px',
                    letterSpacing: 0.3,
                  }}
                  title="This is a reprogram lead — equipment replacement request"
                >
                  ↺ REPROGRAM
                </span>
              )}
            </div>
          </div>

          <div className="sdm-header-actions">
            {bannerErr && <span className="sdm-banner sdm-banner-error">{bannerErr}</span>}
            {bannerOk && <span className="sdm-banner sdm-banner-ok">{bannerOk}</span>}
            <button className="sdm-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="sdm-body">
          {loadingExisting ? (
            <div className="sdm-loading">Loading…</div>
          ) : (
            <>
              {/* USER DETAILS */}
              <div className="sdm-section">
                <div className="sdm-section-title">User Details</div>
                <div className="sdm-grid">
                  {isAdmin && (
                    <div className="sdm-field sdm-span-2">
                      <label style={{ fontWeight: 700 }}>Created By (Admin)</label>
                      <select
                        value={selectedCreatedBy}
                        onChange={(e) => setSelectedCreatedBy(e.target.value)}
                      >
                        <option value="">— select user —</option>
                        {allUsers.map((u) => (
                          <option key={u.id || u.username} value={u.username}>
                            {u.username} {u.role ? `(${u.role})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="sdm-field">
                    <label>Full Name</label>
                    <input value={form.name} onChange={update("name")} placeholder="Full Name" />
                  </div>

                  <div className="sdm-field">
                    <label>
                      Email <RequiredStar />
                    </label>
                    <input
                      className={hasError("email") ? "sdm-input-error" : ""}
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="Email"
                    />
                  </div>

                  <div className="sdm-field">
                    <label>
                      Phone <RequiredStar />
                    </label>
                    <input
                      className={hasError("phone") ? "sdm-input-error" : ""}
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Phone"
                    />
                  </div>

                  <div className="sdm-field">
                    <label>
                      Status <RequiredStar />
                    </label>
                    <select
                      className={hasError("status") ? "sdm-input-error" : ""}
                      value={form.status}
                      onChange={update("status")}
                    >
                      <option value="" disabled>
                        Select Status
                      </option>
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  {String(form.status || "").toLowerCase() === "approved" && (
                    <>
                      <div className="sdm-field">
                        <label>
                          Merchant Id <RequiredStar />
                        </label>
                        <input
                          className={hasError("merchant_id") ? "sdm-input-error" : ""}
                          value={form.merchant_id}
                          onChange={update("merchant_id")}
                          placeholder="Merchant Id"
                        />
                      </div>

                      <div className="sdm-field">
                        <label>
                          Approved By <RequiredStar />
                        </label>
                        <select
                          className={hasError("approved_by") ? "sdm-input-error" : ""}
                          value={form.approved_by}
                          onChange={update("approved_by")}
                        >
                          <option value="" disabled>
                            Select Approved By
                          </option>
                          <option value="Paysafe">Paysafe</option>
                          <option value="Payarc">Payarc</option>
                        </select>
                      </div>
                      

                       <div className="sdm-field">
                        <label>
                         Tracking
                        </label>
                        <input
                          className={hasError("tracking") ? "sdm-input-error" : ""}
                          value={form.tracking}
                          onChange={update("tracking")}
                          placeholder="Tracking"
                        />
                      </div>
                    </>
                  )}


                  <div className="sdm-field sdm-span-2">
                    <label>Owner Street Address</label>
                    <input value={form.street} onChange={update("street")} placeholder="Street" />
                  </div>

                  <div className="sdm-field">
                    <label>City</label>
                    <input value={form.city} onChange={update("city")} placeholder="City" />
                  </div>

                  <div className="sdm-field">
                    <label>State</label>
                    <input value={form.state} onChange={update("state")} placeholder="State" />
                  </div>

                  <div className="sdm-field">
                    <label>Postal Code</label>
                    <input value={form.postal_code} onChange={update("postal_code")} placeholder="Zip" />
                  </div>

                  <div className="sdm-field">
                    <label>
                      DOB (MM-DD-YYYY or YYYY-MM-DD) <RequiredStar />
                    </label>
                    <input
                      className={hasError("DOB") ? "sdm-input-error" : ""}
                      value={toDateInput(form.DOB)}
                      onChange={update("DOB")}
                      onBlur={() => {
                        const v = form.DOB;
                        if (v && !isValidDateValue(v)) {
                          setFieldErrors((prev) => ({ ...prev, DOB: "Invalid date — use MM-DD-YYYY or YYYY-MM-DD" }));
                        } else {
                          setFieldErrors((prev) => { const next = { ...prev }; delete next.DOB; return next; });
                        }
                      }}
                      placeholder="MM-DD-YYYY"
                    />
                    {hasError("DOB") && (
                      <span style={{ color: "#dc2626", fontSize: 12, marginTop: 3, display: "block" }}>
                        {fieldErrors["DOB"]}
                      </span>
                    )}
                  </div>

                  <div className="sdm-field">
                    <label>
                      SSN (9 digits) {!ssnOptional && <RequiredStar />}
                    </label>
                    <input
                      className={hasError("SSN") ? "sdm-input-error" : ""}
                      value={form.SSN}
                      onChange={update("SSN")}
                      placeholder="#########"
                      maxLength={9}
                      disabled={ssnOptional}
                    />
                    <div className="sdm-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={ssnOptional}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSsnOptional(checked);
                            if (checked) {
                              setForm((f) => ({ ...f, SSN: "" }));
                              setFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.SSN;
                                return next;
                              });
                            }
                          }}
                        />
                        Optional SSN
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* BUSINESS DETAILS */}
              <div className="sdm-section">
                <div className="sdm-section-title">Business Details</div>
                <div className="sdm-grid">
                  <div className="sdm-field">
                    <label>
                      Legal Name <RequiredStar />
                    </label>
                    <input
                      className={hasError("Legal-Name") ? "sdm-input-error" : ""}
                      value={form["Legal-Name"]}
                      onChange={update("Legal-Name")}
                      placeholder="Legal Name"
                    />
                  </div>

                  <div className="sdm-field">
                    <label>
                      DBA Name <RequiredStar />
                    </label>
                    <input
                      className={hasError("DBA-Name") ? "sdm-input-error" : ""}
                      value={form["DBA-Name"]}
                      onChange={update("DBA-Name")}
                      placeholder="DBA Name"
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Tax ID</label>
                    <input value={form["Tax-id"]} onChange={update("Tax-id")} placeholder="Tax ID" />
                  </div>

                  <div className="sdm-field">
                    <label>Website</label>
                    <input value={form.Website} onChange={update("Website")} placeholder="Website" />
                  </div>

                  <div className="sdm-field">
                    <label>
                      Ownership Type <RequiredStar />
                    </label>
                    <select
                      className={hasError("ownership-type") ? "sdm-input-error" : ""}
                      value={form["ownership-type"]}
                      onChange={update("ownership-type")}
                    >
                      <option value="" disabled>
                        Select Ownership Type
                      </option>
                      <option value="Sole-Proprietorship">Sole Proprietorship</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Corporation">Corporation</option>
                      <option value="LLC">LLC</option>
                      <option value="Non-Profit">Non-Profit</option>
                      <option value="Government">Government</option>
                    </select>
                  </div>

                  <div className="sdm-field">
                    <label>
                      Business Start Date (MM-DD-YYYY or YYYY-MM-DD) <RequiredStar />
                    </label>
                    <input
                      className={hasError("Business-start-date") ? "sdm-input-error" : ""}
                      value={toDateInput(form["Business-start-date"])}
                      onChange={update("Business-start-date")}
                      onBlur={() => {
                        const v = form["Business-start-date"];
                        if (v && !isValidDateValue(v)) {
                          setFieldErrors((prev) => ({ ...prev, "Business-start-date": "Invalid date — use MM-DD-YYYY or YYYY-MM-DD" }));
                        } else {
                          setFieldErrors((prev) => { const next = { ...prev }; delete next["Business-start-date"]; return next; });
                        }
                      }}
                      placeholder="MM-DD-YYYY"
                    />
                    {hasError("Business-start-date") && (
                      <span style={{ color: "#dc2626", fontSize: 12, marginTop: 3, display: "block" }}>
                        {fieldErrors["Business-start-date"]}
                      </span>
                    )}
                  </div>

                  <div className="sdm-field sdm-span-2">
                    <div className="sdm-checkbox" style={{ marginBottom: 6 }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={sameAsOwner}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSameAsOwner(checked);
                            if (checked) {
                              setForm((f) => {
                                // Use whichever address is filled; owner fields take priority
                                const addr = f.street || f["Business-Address"];
                                const city = f.city || f["Business-City"];
                                const state = f.state || f["Business-State"];
                                const zip = f.postal_code || f["Business-zip"];
                                return {
                                  ...f,
                                  street: addr,
                                  city,
                                  state,
                                  postal_code: zip,
                                  "Business-Address": addr,
                                  "Business-City": city,
                                  "Business-State": state,
                                  "Business-zip": zip,
                                };
                              });
                            }
                          }}
                        />
                        {" "}Use same address as owner
                      </label>
                    </div>
                    <label>
                      Business Address <RequiredStar />
                    </label>
                    <input
                      className={hasError("Business-Address") ? "sdm-input-error" : ""}
                      value={form["Business-Address"]}
                      onChange={(e) => { setSameAsOwner(false); update("Business-Address")(e); }}
                      placeholder="Business Street"
                      disabled={sameAsOwner}
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Business City</label>
                    <input
                      value={form["Business-City"]}
                      onChange={(e) => { setSameAsOwner(false); update("Business-City")(e); }}
                      placeholder="City"
                      disabled={sameAsOwner}
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Business State</label>
                    <input
                      value={form["Business-State"]}
                      onChange={(e) => { setSameAsOwner(false); update("Business-State")(e); }}
                      placeholder="State"
                      disabled={sameAsOwner}
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Business Zip</label>
                    <input
                      value={form["Business-zip"]}
                      onChange={(e) => { setSameAsOwner(false); update("Business-zip")(e); }}
                      placeholder="Zip"
                      disabled={sameAsOwner}
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Business Phone</label>
                    <input value={form["Business-Phone"]} onChange={update("Business-Phone")} placeholder="Business Phone" />
                  </div>

                  <div className="sdm-field">
                    <label>Average Ticket</label>
                    <input value={form["Avg-ticket"]} onChange={update("Avg-ticket")} placeholder="Avg Ticket" />
                  </div>

                  <div className="sdm-field">
                    <label>High Ticket</label>
                    <input value={form["High-ticket"]} onChange={update("High-ticket")} placeholder="High Ticket" />
                  </div>

                  <div className="sdm-field">
                    <label>Total Monthly Processing</label>
                    <input
                      value={form["total-monthly-processing"]}
                      onChange={update("total-monthly-processing")}
                      placeholder="Total monthly processing"
                    />
                  </div>

                  <div className="sdm-field sdm-span-2">
                    <label>Description of Products Sold <RequiredStar /></label>
                    <input
                      className={hasError("products-sold") ? "sdm-input-error" : ""}
                      value={form["products-sold"]}
                      onChange={update("products-sold")}
                      placeholder="Description of products sold"
                    />
                  </div>
                </div>
              </div>

              {/* PRICING */}
              <div className="sdm-section">
                <div className="sdm-section-title">Pricing</div>
                <div className="sdm-grid">
                  <div className="sdm-field">
                    <label>
                      Fee Structure <RequiredStar />
                    </label>
                    <select
                      className={hasError("feestructure") ? "sdm-input-error" : ""}
                      value={form.feestructure}
                      onChange={update("feestructure")}
                    >
                      <option value="" disabled>
                        Select Fee Structure
                      </option>
                      <option value="interchange">Interchange Plus</option>
                      <option value="flatfee">Flat Fee</option>
                      <option value="surcharging">Surcharging</option>
                    </select>
                  </div>

                  <div className="sdm-field">
                    <label>Monthly Fee</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={form["monthly-fee"]}
                      onChange={update("monthly-fee")}
                      placeholder="Monthly fee"
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Monthly Minimum</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={form["monthly-minimum"]}
                      onChange={update("monthly-minimum")}
                      placeholder="Monthly minimum"
                    />
                  </div>


                  {form.feestructure === "surcharging" && (
                    <div className="sdm-field">
                      <label>
                        Pricing <RequiredStar />
                      </label>
                      <select
                        className={hasError("Pricing") ? "sdm-input-error" : ""}
                        value={form.Pricing}
                        onChange={update("Pricing")}
                      >
                        <option value="" disabled>
                          Select Pricing
                        </option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                    </div>
                  )}

                  {(form.feestructure === "interchange" || form.feestructure === "flatfee") && (
                    <div className="sdm-field sdm-span-2">
                      <label>
                        Pricing <RequiredStar />
                      </label>
                      <input
                        className={hasError("PricingText") ? "sdm-input-error" : ""}
                        value={form.PricingText}
                        onChange={update("PricingText")}
                        placeholder="Pricing text"
                      />
                    </div>
                  )}

                  {(form.feestructure === "surcharging" ||
                    form.feestructure === "interchange" ||
                    form.feestructure === "flatfee") && (
                      <div className="sdm-field">
                        <label>
                          Fee <RequiredStar />
                        </label>
                        <input
                          className={hasError("Pricing-variable") ? "sdm-input-error" : ""}
                          value={form["Pricing-variable"]}
                          onChange={update("Pricing-variable")}
                          placeholder="Pricing fee"
                        />
                      </div>
                    )}
                </div>
              </div>

              {/* BANKING */}
              <div className="sdm-section">
                <div className="sdm-section-title">Banking</div>
                <div className="sdm-grid">
                  <div className="sdm-field">
                    <label>
                      Bank Name <RequiredStar />
                    </label>
                    <input
                      className={hasError("Bank-name") ? "sdm-input-error" : ""}
                      value={form["Bank-name"]}
                      onChange={update("Bank-name")}
                      placeholder="Bank name"
                    />
                  </div>

                  <div className="sdm-field">
                    <label>Bank Account</label>
                    <input value={form["Bank-acc"]} onChange={update("Bank-acc")} placeholder="Account number" />
                  </div>

                  <div className="sdm-field">
                    <label>
                      Routing Number <RequiredStar />
                    </label>
                    <input
                      className={hasError("Routing") ? "sdm-input-error" : ""}
                      value={form.Routing}
                      onChange={update("Routing")}
                      placeholder="Routing"
                    />
                  </div>
                </div>
              </div>

              {/* EQUIPMENT + SHIPPING */}
              <div className="sdm-section">
                <div className="sdm-section-title">Equipment & Shipping</div>
                <div className="sdm-grid">
                  <div className="sdm-field">
                    <label>Equipment</label>
                    <select value={form.equipement} onChange={(e) => onEquipmentSelect(e.target.value)}>
                      <option value="" disabled>
                        Select Equipment
                      </option>
                      <option value="paxa920">PAX A920</option>
                      <option value="dejavoo-qd4">Dejavoo QD4</option>
                      <option value="clover-go">Clover Go</option>
                      <option value="clover-flex">Clover Flex</option>
                      <option value="clover-mini">Clover Mini</option>
                      <option value="clover-mini-with-Cashdrawer">Clover Mini with Cash Drawer</option>
                      <option value="clover-station">Clover Station</option>
                      <option value="clover-station-duo">Clover Station Duo</option>
                    </select>
                  </div>

                  {/* equipment counts (kept for payload; hidden like DocumentModal.jsx) */}
                  <input value={form.paxa920} readOnly aria-readonly="true" hidden />
                  <input value={form["dejavoo-qd4"]} readOnly aria-readonly="true" hidden />
                  <input value={form["clover-go"]} readOnly aria-readonly="true" hidden />
                  <input value={form["clover-flex"]} readOnly aria-readonly="true" hidden />
                  <input value={form["clover-mini"]} readOnly aria-readonly="true" hidden />
                  <input value={form["clover-mini-with-Cashdrawer"]} readOnly aria-readonly="true" hidden />
                  <input value={form["clover-station"]} readOnly aria-readonly="true" hidden />
                  <input value={form["clover-station-duo"]} readOnly aria-readonly="true" hidden />

                  {/* shipping source selector */}
                  <div className="sdm-field sdm-span-2">
                    <label>Shipping Address Source</label>
                    <select
                      value={shippingSource}
                      onChange={(e) => {
                        const src = e.target.value;
                        setShippingSource(src);
                        setForm((f) => applyShippingSource(src, f));
                      }}
                    >
                      <option value="manual">Manual (enter below)</option>
                      <option value="business">Use Business Address</option>
                      <option value="home">Use Home Address</option>
                    </select>
                  </div>

                  <div className="sdm-field sdm-span-2">
                    <label>Shipping Address</label>
                    <input
                      value={form["Shipping-Address"]}
                      onChange={update("Shipping-Address")}
                      placeholder="Shipping address"
                    />
                  </div>


                  <div className="sdm-field">
                    <label>Shipping City</label>
                    <input value={form["Shipping-City"]} onChange={update("Shipping-City")} placeholder="City" />
                  </div>

                  <div className="sdm-field">
                    <label>Shipping State</label>
                    <input value={form["Shipping-State"]} onChange={update("Shipping-State")} placeholder="State" />
                  </div>

                  <div className="sdm-field">
                    <label>Shipping Zip</label>
                    <input value={form["Shipping-zip"]} onChange={update("Shipping-zip")} placeholder="Zip" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sdm-footer">
          <button className="sdm-btn" onClick={onClose} disabled={saving}>
            Close
          </button>

          <button className="sdm-btn sdm-btn-upload" onClick={uploadToPandaDoc} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload to PandaDoc"}
          </button>


          <button className="sdm-btn sdm-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
